/**
 * Ask-about-this-run chat for the review page (TRA-917).
 *
 * Streams from `POST /api/review/chat`, which rebuilds the trajectory from
 * the trace key server-side — so unlike the model playground this hook sends
 * no transcript, only the reviewer's own questions. There is no model to
 * pick either: the endpoint uses the platform's judge model.
 *
 * The thread is persisted per trace in sessionStorage (see `lib/chatThread`)
 * so navigating between queue items and back resumes the conversation. The
 * server stays stateless: the whole history is replayed on every turn.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { apiHeaders, apiUrl, getImpersonateTeamId } from '@/api/client'
import { clearThread, loadThread, saveThread } from '@/lib/chatThread'
import { readErrorDetail, readSseStream } from '@/lib/sse'
import type { Message } from '@/hooks/useStreamingChat'

export interface UseReviewChatReturn {
  messages: Message[]
  sendMessage: (content: string) => Promise<void>
  isStreaming: boolean
  error: string | null
  stop: () => void
  clearMessages: () => void
}

/** Drop a trailing assistant placeholder that never received a token. */
function withoutEmptyPlaceholder(messages: Message[]): Message[] {
  const last = messages[messages.length - 1]
  if (last && last.role === 'assistant' && last.content === '') {
    return messages.slice(0, -1)
  }
  return messages
}

/**
 * `opener` is the question the panel sends by itself on open. A lone
 * unanswered copy of it is not restored, so the next mount can ask again;
 * a lone question the reviewer typed is (see `withoutUnansweredOpener`).
 */
export function useReviewChat(traceKey: string, opener: string): UseReviewChatReturn {
  // Lazy initializer: the stored thread is read once per mount, not on
  // every render. The panel is keyed by trace, so this IS the restore.
  const [messages, setMessages] = useState<Message[]>(() => loadThread(traceKey, opener))
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Persist only once the stream settles. `messages` changes on every token,
  // and sessionStorage writes are synchronous — saving per delta would
  // stringify the whole thread and block the main thread mid-answer.
  useEffect(() => {
    if (isStreaming) return
    saveThread(traceKey, messages)
  }, [isStreaming, messages, traceKey])

  // The unmount flush reads this rather than `messages` directly: a cleanup
  // that closed over the render it was registered on would write back that
  // render's thread, undoing everything streamed since.
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  // The panel is keyed by trace and mounted conditionally, so j/k navigation,
  // the Ask toggle, and a trajectory reload all unmount mid-answer. Without
  // this the SSE request keeps streaming against the platform's key with
  // nobody reading it, and the effect above — which skips writes while
  // streaming — never records the question the reviewer just asked.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      // Keep whatever streamed in, matching the Stop button: a partial answer
      // is still evidence. The empty placeholder is dropped so a thread
      // abandoned before the first token does not restore a blank turn.
      const settled = withoutEmptyPlaceholder(messagesRef.current)
      // An empty thread here means "nothing was ever asked", not "clear it" —
      // saveThread would remove the stored key and lose a real thread.
      if (settled.length > 0) saveThread(traceKey, settled)
    }
  }, [traceKey])

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsStreaming(false)
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
    clearThread(traceKey)
  }, [traceKey])

  const sendMessage = useCallback(
    async (content: string) => {
      // Asking spends the platform's model budget, and impersonation is
      // read-only (the backend 403s the POST) — say so instead of surfacing
      // a raw network error.
      if (getImpersonateTeamId()) {
        setError('Ask is disabled while impersonating a team (read-only).')
        return
      }

      const userMessage: Message = { role: 'user', content }
      const history = [...messages, userMessage]
      setMessages([...history, { role: 'assistant', content: '' }])
      setIsStreaming(true)
      setError(null)

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const response = await fetch(apiUrl('/api/review/chat'), {
          method: 'POST',
          headers: apiHeaders({ 'Content-Type': 'application/json' }),
          signal: abortController.signal,
          // Sent whole, deliberately un-trimmed: `trimThread` bounds storage
          // only. A thread at `MAX_STORED_MESSAGES` posts one over the
          // server's cap and is rejected — the reviewer clears it and starts
          // a fresh one rather than have us quietly answer from a context
          // they can no longer see.
          body: JSON.stringify({
            trace_key: traceKey,
            messages: history.map((m) => ({ role: m.role, content: m.content })),
          }),
        })

        if (!response.ok) {
          throw new Error(await readErrorDetail(response))
        }
        if (!response.body) {
          throw new Error('Response body is not readable')
        }

        await readSseStream(
          response.body,
          (event) => {
            switch (event.type) {
              case 'text_delta': {
                setMessages((prev) => {
                  const updated = [...prev]
                  const lastIdx = updated.length - 1
                  const lastMsg = updated[lastIdx]
                  if (lastMsg && lastMsg.role === 'assistant') {
                    updated[lastIdx] = {
                      role: 'assistant',
                      content: lastMsg.content + event.content,
                    }
                  }
                  return updated
                })
                break
              }
              case 'thinking_delta':
              case 'tool_call_start':
              case 'tool_call_delta':
              case 'done': {
                // The reviewer wants the answer, not the model's scratchpad.
                break
              }
              case 'error': {
                setError(event.content)
                setMessages(withoutEmptyPlaceholder)
                break
              }
            }
          },
          (raw) => {
            console.error('[useReviewChat] Failed to parse SSE event:', raw)
          },
        )
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // A stopped answer keeps whatever streamed in — it is still evidence.
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to send message')
        setMessages(withoutEmptyPlaceholder)
      } finally {
        setIsStreaming(false)
        abortControllerRef.current = null
      }
    },
    [traceKey, messages],
  )

  return { messages, sendMessage, isStreaming, error, stop, clearMessages }
}
